import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { tradingPlatformLink } from '../../utils/tradingPlatform';
import { ChevronDown, ChevronLeft, ChevronRight, Menu, X, Globe, ArrowRight, Search, Bell, User, Landmark,
  BarChart3, Package, LineChart, UserCircle, DollarSign, CreditCard,
  Banknote, TrendingUp, Gem, Building2, Layers, FileText,
  Smartphone, Monitor, Tablet, Laptop, CandlestickChart, Repeat,
  Scale, Newspaper, GraduationCap, Video, BookOpen, Hash,
  Calendar, Smile, Server, Activity, Handshake, UserPlus, Gift,
  Wallet, Users, Award, ShoppingBasket, Sparkles, Megaphone, Shield, Leaf, Briefcase, Trophy,
  Phone, HelpCircle, BookMarked, Star, Target, Zap, Calculator,
  Ruler, Coins, RefreshCw, Building, Rocket } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import MobileMenu from './MobileMenu';
import AnimatedMenuIcon from '../marketing/AnimatedMenuIcon';
import MobileAppBanner from './MobileAppBanner';
import GlobalSearch from '../common/GlobalSearch';

/**
 * VAROTRADES — Vantage-style LIGHT Header
 * - Cream background, no live price tape
 * - Premium morphing pill on scroll
 * - Mega Menu items each have a generated image
 */

const API = process.env.REACT_APP_BACKEND_URL;
const IMG = (n) => `${API}/api/media/generated/${n}`;
const IMG2 = (n) => `${API}/api/media/generated/megav2/${n}`;

const NAV_DATA = (isRTL) => ([
  /* ─────────── 1. TRADING ─────────── */
  {
    id: 'trading',
    label: isRTL ? 'التداول' : 'Trading',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'الوصول إلى أكثر من 2,000 عقد فروقات عبر الأسواق العالمية' : 'Access 2,000+ CFDs across global markets', link: '/markets-hub' },
      { icon: Package, label: isRTL ? 'كل منتجات التداول' : 'All Trading Products', desc: isRTL ? 'اكتشف منتجاتنا الواسعة للتداول' : 'Discover our wide range of products to trade', link: '/trading/products' },
      {
        icon: LineChart, label: isRTL ? 'الأسواق' : 'Markets', desc: isRTL ? 'الوصول إلى أحدث عروض المنتجات' : 'Access the latest product offerings', link: '/markets-hub',
        children: [
          {
            icon: Banknote, label: isRTL ? 'الفوركس' : 'Forex', desc: isRTL ? 'تداول الأزواج الرئيسية والثانوية والغريبة' : 'Trade major, minor, and exotic currency pairs',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على الفوركس' : 'Forex Overview', link: '/markets/forex/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول الفوركس؟' : 'What is Forex Trading?', link: '/markets/forex/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول الفوركس' : 'How to Trade Forex', link: '/markets/forex/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول الفوركس' : 'Why Trade Forex', link: '/markets/forex/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول الفوركس' : 'Forex Trading Strategies', link: '/markets/forex/strategies' },
            ],
          },
          { icon: LineChart, label: isRTL ? 'المؤشرات' : 'Indices', desc: isRTL ? 'تداول أعلى المؤشرات مثل DJ 30 و S&P 500 و DAX' : 'Trade top indices like DJ 30, S&P 500 and DAX', link: '/markets/indices',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على المؤشرات' : 'Indices Overview', link: '/markets/indices/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول المؤشرات؟' : 'What is Indices Trading?', link: '/markets/indices/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول المؤشرات' : 'How to Trade Indices', link: '/markets/indices/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول المؤشرات' : 'Why Trade Indices', link: '/markets/indices/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول المؤشرات' : 'Indices Trading Strategies', link: '/markets/indices/strategies' },
            ],
          },
          { icon: Gem, label: isRTL ? 'السلع' : 'Commodities', desc: isRTL ? 'تداول المواد الخام مثل الذهب والنفط والفضة' : 'Trade raw materials like gold, oil, and silver', link: '/markets/commodities',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على السلع' : 'Commodities Overview', link: '/markets/commodities/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول السلع؟' : 'What is Commodities Trading?', link: '/markets/commodities/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول السلع' : 'How to Trade Commodities', link: '/markets/commodities/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول السلع' : 'Why Trade Commodities', link: '/markets/commodities/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول السلع' : 'Commodities Trading Strategies', link: '/markets/commodities/strategies' },
            ],
          },
          { icon: Building2, label: isRTL ? 'الأسهم CFD' : 'Shares CFD', desc: isRTL ? 'تداول أسهم الشركات العالمية في US و UK و EU و AU' : 'Trade shares of global companies in US, UK, EU and AU', link: '/markets/shares',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على الأسهم' : 'Shares CFD Overview', link: '/markets/shares/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول الأسهم؟' : 'What is Shares CFD Trading?', link: '/markets/shares/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول الأسهم' : 'How to Trade Shares CFD', link: '/markets/shares/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول الأسهم' : 'Why Trade Shares CFD', link: '/markets/shares/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول الأسهم' : 'Shares CFD Strategies', link: '/markets/shares/strategies' },
            ],
          },
          { icon: Layers, label: 'ETFs', desc: isRTL ? 'تنوّع مع سلال من الأسهم والسلع والسندات' : 'Diversify with baskets of stocks, commodities, or bonds', link: '/markets/etfs',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على ETFs' : 'ETFs Overview', link: '/markets/etfs/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول ETFs؟' : 'What is ETF Trading?', link: '/markets/etfs/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول ETFs' : 'How to Trade ETFs', link: '/markets/etfs/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول ETFs' : 'Why Trade ETFs', link: '/markets/etfs/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول ETFs' : 'ETF Trading Strategies', link: '/markets/etfs/strategies' },
            ],
          },
          { icon: FileText, label: isRTL ? 'السندات' : 'Bonds', desc: isRTL ? 'تداول الأوراق المالية بما فيها سندات الخزانة الأمريكية لـ 10 سنوات' : 'Trade debt securities, including US 10-year Treasury bonds', link: '/markets/bonds',
            children: [
              { icon: BarChart3, label: isRTL ? 'نظرة عامة على السندات' : 'Bonds Overview', link: '/markets/bonds/overview' },
              { icon: HelpCircle, label: isRTL ? 'ما هو تداول السندات؟' : 'What is Bonds Trading?', link: '/markets/bonds/what-is' },
              { icon: BookMarked, label: isRTL ? 'كيف تتداول السندات' : 'How to Trade Bonds', link: '/markets/bonds/how-to' },
              { icon: Star, label: isRTL ? 'لماذا تتداول السندات' : 'Why Trade Bonds', link: '/markets/bonds/why' },
              { icon: Target, label: isRTL ? 'استراتيجيات تداول السندات' : 'Bonds Trading Strategies', link: '/markets/bonds/strategies' },
            ],
          },
        ],
      },
      {
        icon: UserCircle, label: isRTL ? 'حسابات التداول' : 'Trading Accounts', desc: isRTL ? 'مصممة للمتداولين من جميع المستويات' : 'Tailored for traders of all experience levels',
        children: [
          { icon: Star, label: 'Pro ECN', desc: isRTL ? 'ظروف تداول مثالية' : 'Optimum trading conditions', badge: 'NEW', link: '/accounts/pro-ecn' },
          { icon: Zap, label: 'Raw ECN', desc: isRTL ? 'فروق حادة وعمولات منخفضة' : 'Razor spreads, low commission', link: '/accounts/raw-ecn' },
          { icon: GraduationCap, label: 'Standard STP', desc: isRTL ? 'مثالي للمبتدئين' : 'Ideal for beginners', link: '/accounts/standard' },
          { icon: BookMarked, label: isRTL ? 'حساب إسلامي (Swap-Free)' : 'Swap-Free Account', desc: isRTL ? 'متوافق مع الشريعة' : 'Sharia compliant', link: '/accounts/swap-free' },
        ],
      },
      {
        icon: DollarSign, label: isRTL ? 'رسوم التداول' : 'Trading Fees', desc: isRTL ? 'تكاليف التداول لتنفيذ أوامر الشراء أو البيع' : 'Trading costs for executing buy or sell orders',
        children: [
          { icon: Ruler, label: isRTL ? 'الفروقات' : 'Spreads', desc: isRTL ? 'الفرق بين العرض والطلب' : 'Bid-ask difference', link: '/fees/spreads' },
          { icon: Coins, label: isRTL ? 'العمولات' : 'Commissions', desc: isRTL ? 'تكاليف التنفيذ' : 'Execution costs', link: '/fees/commissions' },
          { icon: RefreshCw, label: isRTL ? 'أسعار التبييت' : 'Swap Rates', desc: isRTL ? 'رسوم الاحتفاظ بالمراكز' : 'Overnight financing', link: '/fees/swap-rates' },
          { icon: Calculator, label: isRTL ? 'حاسبة التداول' : 'Trading Calculator', desc: isRTL ? 'احسب الأرباح والمخاطر' : 'Compute profit & risk', link: '/fees/calculator' },
        ],
      },
      { icon: CreditCard, label: isRTL ? 'الإيداع والسحب' : 'Deposit and Withdrawal', desc: isRTL ? 'اكتشف كل طرق التمويل والسحب لحسابك' : 'Discover all funding and withdrawal methods for your account', link: '/funds/deposit' },
    ],
  },

  /* ─────────── 2. PLATFORMS ─────────── */
  {
    id: 'platforms',
    label: isRTL ? 'المنصات' : 'Platforms',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'الوصول إلى منصات متنوعة لخيارات تداول متعددة' : 'Access various platforms for diverse trading options', link: '/platforms' },
      { icon: Rocket, label: isRTL ? 'تطبيق VAROTRADES' : 'VAROTRADES App', desc: isRTL ? 'تداول الأسواق العالمية في أي وقت ومن أي مكان' : 'Trade global markets anytime, anywhere with VAROTRADES App', link: '/platforms/app' },
      { icon: Laptop, label: isRTL ? 'منصة Web Trading' : 'VAROTRADES Web Trading', desc: isRTL ? 'كل صفقاتك ومخططاتك ومراكزك بتسجيل دخول واحد آمن' : 'Access all of your trades, charts and positions in one secure login', badge: 'NEW', link: '/platforms/web' },
      { icon: LineChart, label: 'TradingView', desc: isRTL ? 'تداول أذكى على المنصة الرائدة عالمياً للرسوم البيانية' : 'Trade smarter on the leading global charting platform', link: '/platforms/tradingview' },
      { icon: Repeat, label: isRTL ? 'نسخ التداول' : 'Copy Trading', desc: isRTL ? 'انسخ، تداول، اربح بنقرة واحدة' : 'Copy, trade, and earn in one tap', badge: 'Popular', link: '/copy-trading' },
    ],
  },

  /* ─────────── 3. ACADEMY ─────────── */
  {
    id: 'academy',
    label: isRTL ? 'الأكاديمية' : 'Academy',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'تعلّم كل شيء عن التداول مع أكاديمية VAROTRADES' : 'Learn all about trading with VAROTRADES Academy', link: '/learn' },
      { icon: Scale, label: isRTL ? 'تعريفات ترامب الجمركية' : 'Trump Tariffs', desc: isRTL ? 'تابع تحركات ترامب التجارية والتعريفات وتأثيرها الاقتصادي' : "Stay on top of Trump's trade moves, tariffs, and its economic impact", link: '/learn/trump-tariffs' },
      { icon: Newspaper, label: isRTL ? 'كل المقالات' : 'All Articles', desc: isRTL ? 'اكتشف نصائح التداول والأدلة والرؤى التعليمية' : 'Discover trading tips, guides, and educational insights', link: '/learn/articles' },
      { icon: BookOpen, label: isRTL ? 'الكتب الإلكترونية' : 'eBooks', desc: isRTL ? 'صقل معرفتك في التداول مع كتب إلكترونية مجانية' : 'Sharpen your trading knowledge with free eBooks', link: '/learn/ebooks' },
      { icon: Hash, label: isRTL ? 'المصطلحات' : 'Terminology', desc: isRTL ? 'تعلم المصطلحات والمفاهيم الرئيسية في التمويل' : 'Learn key terms and concepts in finance', link: '/learn/glossary' },
    ],
  },

  /* ─────────── 4. MARKET ANALYSIS ─────────── */
  {
    id: 'analysis',
    label: isRTL ? 'تحليل السوق' : 'Market Analysis',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'تحليلات السوق وآخر الأخبار في مكان واحد' : 'Market analysis and latest news', link: '/learn/analysis' },
      { icon: Calendar, label: isRTL ? 'التقويم الاقتصادي' : 'Economic Calendar', desc: isRTL ? 'تابع أحداث السوق الرئيسية' : 'Track key market events', link: '/analysis/calendar' },
      { icon: Newspaper, label: isRTL ? 'الأخبار والتحليلات' : 'News and Analysis', desc: isRTL ? 'كن في المقدمة بآخر التحليلات' : 'Stay ahead with the latest analysis', link: '/analysis/news' },
      { icon: Smile, label: isRTL ? 'مشاعر العملاء' : 'Client Sentiment', desc: isRTL ? 'قِس اتجاهات السوق' : 'Gauge market trends', link: '/analysis/sentiment' },
      { icon: Server, label: 'Forex VPS', desc: isRTL ? 'سيرفر آمن للاتصال غير المنقطع' : 'Uninterrupted connectivity for trading', link: '/analysis/vps' },
      { icon: LineChart, label: isRTL ? 'إشارات التداول' : 'Trading Signals', desc: isRTL ? 'رؤى مبنية على التحليل الفني' : 'Technical analysis insights', link: '/analysis/signals' },
      { icon: Bell, label: isRTL ? 'الإشعارات' : 'Notifications', desc: isRTL ? 'تحديثات فورية لأخبار المنتجات' : 'Real-time product news updates', link: '/analysis/notifications' },
    ],
  },

  /* ─────────── 5. PARTNER PROGRAM ─────────── */
  {
    id: 'partners',
    label: isRTL ? 'برنامج الشراكة' : 'Partner Program',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'حوّل خبرتك إلى أرباح مع برنامج الشراكة' : 'Turn your expertise into earnings', link: '/partners' },
      { icon: UserCircle, label: isRTL ? 'برنامج Affiliate' : 'Affiliate Program', desc: isRTL ? 'أحِل العملاء واربح عمولات رائدة' : 'Refer clients and earn top commissions', badge: 'Popular', link: '/partners/affiliate' },
      { icon: Handshake, label: isRTL ? 'الوسطاء التعريفيون (IB)' : 'Introducing Brokers', desc: isRTL ? 'عمولات IB من الطراز الأول وحصة إيرادات تصل لـ 70%' : 'Top-tier IB commissions, up to 70% rev share', link: '/partners/ib' },
      { icon: Briefcase, label: isRTL ? 'مدير الأموال (PAMM)' : 'Money Manager (PAMM)', desc: isRTL ? 'ادر أموال عملائك من حساب واحد' : "Manage your clients' funds from one account", link: '/partners/money-manager' },
    ],
  },

  /* ─────────── 6. PROMOTIONS ─────────── */
  {
    id: 'promotions',
    label: isRTL ? 'العروض' : 'Promotions',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'تصفح كل العروض الحالية' : 'Check all our current promotions', link: '/promotions' },
      { icon: Wallet, label: isRTL ? 'بونص الترحيب 50%' : '50% Welcome Bonus', desc: isRTL ? 'بونص يضاعف أوّل إيداع لك حتى $5,000' : 'Doubles your first deposit up to $5,000', badge: 'Popular', link: '/promotions/welcome-bonus' },
      { icon: Trophy, label: isRTL ? 'مسابقات التداول' : 'Trading Contests', desc: isRTL ? 'سباقات شهرية بجوائز تصل إلى $50,000' : 'Monthly contests with prizes up to $50,000', badge: 'NEW', link: '/promotions/contests' },
      { icon: Users, label: isRTL ? 'أحِل صديقاً' : 'Refer a Friend', desc: isRTL ? 'اربح بونصات إضافية مع كل إحالة' : 'Earn extra bonuses for every referral', link: '/promotions/refer' },
      { icon: Award, label: isRTL ? 'مكافآت VAROTRADES' : 'VAROTRADES Rewards', desc: isRTL ? 'اربح V-Points مع كل صفقة' : 'Earn V-Points with every trade', link: '/promotions/rewards' },
    ],
  },

  /* ─────────── 7. ABOUT ─────────── */
  {
    id: 'about',
    label: isRTL ? 'عن الشركة' : 'About',
    items: [
      { icon: BarChart3, label: isRTL ? 'نظرة عامة' : 'Overview', desc: isRTL ? 'اكتشف كل شيء عن VAROTRADES' : 'Discover all about VAROTRADES', link: '/about' },
      { icon: Megaphone, label: isRTL ? 'مركز الإعلام' : 'Media Centre', desc: isRTL ? 'موادنا التسويقية' : 'Our brand-related materials', link: '/about/media' },
      { icon: Sparkles, label: isRTL ? 'لماذا VAROTRADES؟' : 'Why VAROTRADES?', desc: isRTL ? 'لماذا يثق المتداولون بنا' : 'Why traders trust us', badge: 'Popular', link: '/about/why' },
      { icon: Handshake, label: isRTL ? 'شركاؤنا' : 'Our Partners', desc: isRTL ? 'تعاون موثوق ومبتكر' : 'Trusted and innovative collaborations', link: '/partners' },
      { icon: Shield, label: isRTL ? 'تأمين الأموال' : 'Fund Insurance', desc: isRTL ? 'تداول بثقة مع حماية الأموال' : 'Trade with confidence with fund protection', link: '/about/insurance' },
      { icon: Leaf, label: 'ESG', desc: isRTL ? 'خطواتنا نحو مستقبل مستدام' : 'Our steps toward a sustainable future', link: '/about/esg' },
      { icon: Megaphone, label: isRTL ? 'البيانات الصحفية' : 'Press Releases', desc: isRTL ? 'كن على اطلاع بآخر إصدارات VAROTRADES' : 'Stay up-to-date with the latest VAROTRADES news', link: '/about/press' },
      { icon: Globe, label: isRTL ? 'سيرفرات التداول' : 'Our Trading Servers', desc: isRTL ? 'محسّنة للتداول السلس' : 'Optimised for seamless trading', link: '/about/servers' },
      { icon: Phone, label: isRTL ? 'تواصل معنا' : 'Contact Us', desc: isRTL ? 'فريق دعم متعدد اللغات 24/7' : '24/7 multilingual support team', link: '/about/contact' },
    ],
  },
]);

/**
 * FlyoutPanel — multi-level nested mega menu (Vantage-style).
 * Column 1: rootItems. Hovering a row with `children` opens column 2 (and so on).
 * Pure text — no images. Items with children show a chevron and reveal a sub-flyout on hover.
 */
/** Recursively flattens nested items for the mobile drawer (with depth tracking) */
const flattenItems = (items, depth = 0) => {
  const out = [];
  items.forEach((it) => {
    out.push({ ...it, _depth: depth });
    if (Array.isArray(it.children)) out.push(...flattenItems(it.children, depth + 1));
  });
  return out;
};

const FlyoutColumn = ({ items, hovered, onHover, onSelect, isRTL, columnLabel }) => (
  <div className="w-[300px] shrink-0 py-2 border-l border-[rgba(20,15,10,0.06)] first:border-l-0" data-testid={`flyout-col-${columnLabel}`}>
    {items.map((it, i) => {
      const hasChildren = Array.isArray(it.children) && it.children.length > 0;
      const isHovered = hovered === i;
      const Inner = (
        <div
          className={`relative flex items-start gap-3 mx-2 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
            isHovered ? 'bg-[rgba(20,15,10,0.05)]' : 'hover:bg-[rgba(20,15,10,0.04)]'
          }`}
        >
          {/* Lucide Icon — black on white (Vantage style) */}
          {it.icon && (
            <div className="shrink-0 w-9 h-9 rounded-lg bg-white border border-[rgba(20,15,10,0.08)] flex items-center justify-center mt-0.5">
              {React.createElement(it.icon, { className: 'w-[18px] h-[18px] text-ink-900', strokeWidth: 1.7 })}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[14px] font-semibold tracking-tight text-ink-900 leading-tight">
                {it.label}
              </p>
              {it.badge && (
                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-white text-[9px] font-bold uppercase tracking-wider leading-none whitespace-nowrap ${
                  it.badge === 'NEW' || it.badge === 'جديد' ? 'bg-[#E63946]' : 'bg-[#E63946]'
                }`}>
                  {it.badge}
                </span>
              )}
            </div>
            {it.desc && <p className="text-ink-500 text-[11.5px] mt-1 leading-snug">{it.desc}</p>}
          </div>
          {hasChildren && (
            <span aria-hidden="true" className="flex items-center self-center shrink-0" data-testid={`flyout-arrow-${columnLabel}-${i}`}>
              {isRTL ? (
                <ChevronLeft className="w-4 h-4 text-flame-600" strokeWidth={2.4} />
              ) : (
                <ChevronRight className="w-4 h-4 text-flame-600" strokeWidth={2.4} />
              )}
            </span>
          )}
        </div>
      );
      return (
        <div
          key={i}
          onMouseEnter={() => onHover(i)}
          onFocus={() => onHover(i)}
          data-testid={`flyout-item-${columnLabel}-${i}`}
        >
          {hasChildren ? (
            Inner
          ) : (
            <Link to={it.link || '#'} onClick={onSelect}>{Inner}</Link>
          )}
        </div>
      );
    })}
  </div>
);

const FlyoutPanel = ({ rootItems, rootKey, isRTL, onSelect }) => {
  const [c1, setC1] = useState(null);
  const [c2, setC2] = useState(null);

  // When user hovers a different col1 item, reset col2 hover
  useEffect(() => { setC2(null); }, [c1]);

  const col2Items = c1 != null && rootItems[c1]?.children ? rootItems[c1].children : null;
  const col3Items = col2Items && c2 != null && col2Items[c2]?.children ? col2Items[c2].children : null;

  return (
    <div className="bg-white rounded-2xl border border-[rgba(20,15,10,0.08)] shadow-[0_30px_80px_rgba(20,15,10,0.18)] overflow-hidden inline-block" data-testid={`flyout-${rootKey}`}>
      <div className="flex items-stretch">
        <FlyoutColumn items={rootItems} hovered={c1} onHover={setC1} onSelect={onSelect} isRTL={isRTL} columnLabel="root" />
        <AnimatePresence mode="wait">
          {col2Items && (
            <motion.div
              key={`c2-${c1}`}
              initial={{ opacity: 0, x: isRTL ? 8 : -8 }}
              animate={{ opacity: 1, x: 0, transition: { duration: 0.18 } }}
              exit={{ opacity: 0, x: isRTL ? 8 : -8, transition: { duration: 0.14 } }}
            >
              <FlyoutColumn items={col2Items} hovered={c2} onHover={setC2} onSelect={onSelect} isRTL={isRTL} columnLabel="c2" />
            </motion.div>
          )}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {col3Items && (
            <motion.div
              key={`c3-${c2}`}
              initial={{ opacity: 0, x: isRTL ? 8 : -8 }}
              animate={{ opacity: 1, x: 0, transition: { duration: 0.18 } }}
              exit={{ opacity: 0, x: isRTL ? 8 : -8, transition: { duration: 0.14 } }}
              className="bg-gray-50"
              data-testid="flyout-col3-wrapper"
            >
              <FlyoutColumn items={col3Items} hovered={null} onHover={() => {}} onSelect={onSelect} isRTL={isRTL} columnLabel="c3" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const Header = () => {
 const [scrolled, setScrolled] = useState(false);
 const [openMenu, setOpenMenu] = useState(null);
 const [menuLeft, setMenuLeft] = useState(0);
 const [mobileOpen, setMobileOpen] = useState(false);
 const [profileOpen, setProfileOpen] = useState(false);
 const [langOpen, setLangOpen] = useState(false);
 const [searchOpen, setSearchOpen] = useState(false);
 const closeTimer = useRef(null);
 const buttonRefs = useRef({});
 const { isAuthenticated, isAdmin, logout, user } = useAuth();
 const { t, language, changeLanguage, availableLanguages, direction, isRTL } = useLanguage();
 const navigate = useNavigate();
 const items = NAV_DATA(isRTL);

 useEffect(() => {
 const onScroll = () => {
 setScrolled(window.scrollY > 24);
 };
 window.addEventListener('scroll', onScroll, { passive: true });
 onScroll();
 return () => window.removeEventListener('scroll', onScroll);
 }, []);

 // close on Esc + ⌘K to open search
 useEffect(() => {
 const onKey = (e) => {
 if (e.key === 'Escape') { setOpenMenu(null); setMobileOpen(false); }
 if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
 e.preventDefault();
 setSearchOpen(v => !v);
 }
 };
 window.addEventListener('keydown', onKey);
 return () => window.removeEventListener('keydown', onKey);
 }, []);

 const scheduleClose = () => { clearTimeout(closeTimer.current); closeTimer.current = setTimeout(() => setOpenMenu(null), 120); };
 const cancelClose = () => clearTimeout(closeTimer.current);

 const openMenuFor = (id) => {
   cancelClose();
   const btn = buttonRefs.current[id];
   if (btn) {
     const rect = btn.getBoundingClientRect();
     setMenuLeft(isRTL ? rect.right : rect.left);
   }
   setOpenMenu(id);
 };
 const handleLogout = () => { logout(); navigate('/'); };

 // ─── Bypass React's synthetic event delegation by attaching NATIVE listeners ───
 // (Lenis or other top-level transforms can sometimes break React's delegated
 // pointer-enter handling after scroll. Native listeners always fire.)
 useEffect(() => {
 const buttons = items.map((it) => ({ id: it.id, el: document.querySelector(`[data-testid="nav-${it.id}"]`) }))
 .filter((b) => b.el);
 if (!buttons.length) return;
 const enterHandlers = [];
 buttons.forEach(({ id, el }) => {
 const onEnter = () => { cancelClose(); setOpenMenu(id); };
 el.addEventListener('pointerenter', onEnter);
 el.addEventListener('mouseenter', onEnter);
 enterHandlers.push({ el, onEnter });
 });
 return () => {
 enterHandlers.forEach(({ el, onEnter }) => {
 el.removeEventListener('pointerenter', onEnter);
 el.removeEventListener('mouseenter', onEnter);
 });
 };
 }, [items]);

 return (
 <>
 {/* PROMO RIBBON — slim 28px (desktop only) */}
 <div className="hidden lg:flex fixed top-0 inset-x-0 z-50 h-7 bg-flame-500 text-white" data-testid="promo-ribbon">
 <div className="h-full max-w-[1440px] mx-auto px-6 flex items-center justify-center text-[11.5px] font-medium w-full">
 <span className="opacity-95">{isRTL ? '🎁 احصل على بونص ترحيبي 50% على أوّل إيداع' : '🎁 Get a 50% welcome bonus on your first deposit'}</span>
 <Link to="/register" className="ms-3 underline underline-offset-2 decoration-white/60 hover:decoration-white inline-flex items-center gap-1" data-testid="promo-cta">
 {isRTL ? 'سجّل الآن' : 'Sign up'} <ArrowRight className={`w-3 h-3 ${isRTL ? 'rotate-180' : ''}`} />
 </Link>
 </div>
 </div>

 {/* MOBILE INSTALL APP BANNER — shown above mobile header */}
 <MobileAppBanner />

 {/* HEADER — clean static Vantage style. Top offset adapts: desktop 28px ribbon · mobile 68px banner (or 0 when dismissed) */}
 <header
 className="fixed inset-x-0 z-50 bg-white/95 backdrop-blur-xl border-b border-[rgba(20,15,10,0.06)] shadow-sm"
 style={{ top: 'var(--vt-header-top, 0px)' }}
 dir={direction}
 data-testid="header"
 >
 <style>{`
 :root { --vt-header-top: 0px; }
 @media (min-width: 1024px) { :root { --vt-header-top: 28px; } }
 `}</style>
 <div className="max-w-[1440px] mx-auto">
 <div className="flex items-center justify-between gap-4 h-16 px-6 lg:px-10">
 {/* Brand */}
 <Link to="/" className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`} data-testid="header-logo">
 <img src={`${API}/api/media/varotrade_official_emblem.png`} alt="VAROTRADES" className="h-8 w-auto" draggable="false" />
 </Link>

 {/* Desktop Nav */}
 <nav className="hidden lg:flex items-center gap-0.5" onMouseLeave={scheduleClose} onMouseEnter={cancelClose}>
 {items.map((item) => (
 <button
 key={item.id}
 ref={(el) => { buttonRefs.current[item.id] = el; }}
 onPointerEnter={() => openMenuFor(item.id)}
 onMouseEnter={() => openMenuFor(item.id)}
 onFocus={() => openMenuFor(item.id)}
 onClick={() => (openMenu === item.id ? setOpenMenu(null) : openMenuFor(item.id))}
 className={`group inline-flex items-center gap-1 px-3 h-10 rounded-full text-[13px] font-medium whitespace-nowrap transition-all ${
 openMenu === item.id ? 'text-flame-600 bg-flame-50' : 'text-ink-800 hover:text-flame-600 hover:bg-flame-50/60'
 }`}
 data-testid={`nav-${item.id}`}
 >
 {item.label}
 </button>
 ))}
 </nav>

 {/* Right cluster */}
 <div className="flex items-center gap-1.5">
 <button onClick={() => setSearchOpen(true)} className="hidden lg:inline-flex items-center gap-2 h-10 px-3.5 rounded-full bg-[rgba(20,15,10,0.04)] hover:bg-flame-50 text-ink-700 hover:text-flame-600 text-[12px] transition-all border border-transparent hover:border-flame-200" data-testid="cmdk-search">
 <Search className="w-3.5 h-3.5" />
 <span>{isRTL ? 'بحث' : 'Search'}</span>
 <kbd className="ms-1 hidden xl:inline-flex h-5 px-1.5 items-center font-mono text-[10px] bg-white border border-[rgba(20,15,10,0.08)] rounded">⌘K</kbd>
 </button>

 <div className="relative">
 <button onClick={() => setLangOpen((v) => !v)} className="hidden md:inline-flex items-center gap-1.5 h-10 px-3 rounded-full text-ink-700 hover:text-flame-600 hover:bg-flame-50 transition-all text-[12.5px]" data-testid="language-selector">
 <Globe className="w-4 h-4" />
 <span>{availableLanguages.find((l) => l.code === language)?.flag}</span>
 </button>
 <AnimatePresence>
 {langOpen && (
 <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} w-48 bg-white rounded-2xl border border-[rgba(20,15,10,0.08)] shadow-2xl overflow-hidden`}>
 {availableLanguages.map((lang) => (
 <button key={lang.code} onClick={() => { changeLanguage(lang.code); setLangOpen(false); }} className={`flex items-center gap-3 w-full px-3 py-2 text-sm transition-all ${language === lang.code ? 'bg-flame-50 text-flame-600' : 'text-ink-700 hover:bg-flame-50/60 hover:text-flame-600'}`} data-testid={`lang-${lang.code}`}>
 <span className="text-lg">{lang.flag}</span><span>{lang.name}</span>
 </button>
 ))}
 </motion.div>
 )}
 </AnimatePresence>
 </div>

 {isAuthenticated ? (
 <>
 <button className="relative p-2 text-ink-700 hover:text-flame-600 hover:bg-flame-50 rounded-full transition-all" data-testid="notifications-btn">
 <Bell className="w-4 h-4" /><span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-flame-500 rounded-full" />
 </button>
 <div className="relative">
 <button onClick={() => setProfileOpen((v) => !v)} className="flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-[rgba(20,15,10,0.04)] hover:bg-flame-50 border border-[rgba(20,15,10,0.06)] hover:border-flame-200 transition-all" data-testid="user-menu-btn">
 <div className="w-7 h-7 rounded-full bg-flame-500 flex items-center justify-center shadow-md ">
 <User className="w-3.5 h-3.5 text-white" />
 </div>
 <ChevronDown className="w-3.5 h-3.5 text-ink-500" />
 </button>
 <AnimatePresence>
 {profileOpen && (
 <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }} className={`absolute top-full mt-2 ${isRTL ? 'left-0' : 'right-0'} w-72 bg-white rounded-2xl border border-[rgba(20,15,10,0.08)] shadow-2xl overflow-hidden p-2`}>
 <div className="px-3 py-3 border-b border-[rgba(20,15,10,0.06)] mb-1">
 <p className="text-ink-900 font-semibold text-sm">{user?.first_name} {user?.last_name}</p>
 <p className="text-ink-500 text-xs">{user?.email}</p>
 </div>
 {[
 { label: isRTL ? 'لوحة التحكم' : 'Dashboard', link: '/dashboard', external: false },
 { label: isRTL ? 'منصة التداول' : 'Trading Platform', external: true },
 ].map((m, i) => {
 const linkProps = m.external ? tradingPlatformLink() : { href: m.link };
 const isExt = m.external && linkProps.target === '_blank';
 return (
 <a key={i} {...linkProps} onClick={() => setProfileOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-flame-50 hover:text-flame-600 text-ink-700 transition-all ${isRTL ? 'flex-row-reverse text-right' : ''}`}>
 <span className="text-sm">{m.label}</span>
 {isExt && <ArrowRight className="w-3 h-3 opacity-60" />}
 </a>
 );
 })}
 {isAdmin && (
 <Link to="/admin" onClick={() => setProfileOpen(false)} className={`flex items-center gap-3 px-3 py-2 rounded-xl bg-flame-50/60 text-flame-600 hover:bg-flame-100 ${isRTL ? 'flex-row-reverse text-right' : ''}`} data-testid="profile-menu-admin">
 <Landmark className="w-4 h-4" /><span className="text-sm">{t('adminPanel')}</span>
 </Link>
 )}
 <button onClick={() => { handleLogout(); setProfileOpen(false); }} className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-flame-50 hover:text-flame-600 text-ink-700 mt-1 ${isRTL ? 'flex-row-reverse text-right' : ''}`} data-testid="logout-btn">
 <X className="w-4 h-4" /><span className="text-sm">{t('logout')}</span>
 </button>
 </motion.div>
 )}
 </AnimatePresence>
 </div>
 </>
 ) : (
 <>
 <Link to="/login" className="hidden lg:inline-flex items-center h-10 px-4 text-[13.5px] font-medium text-ink-800 hover:text-flame-600 transition-colors" data-testid="login-link">
 {t('login')}
 </Link>
 <Link to="/register" data-testid="register-btn">
 <button className="varo-btn-primary h-10 text-[13.5px] px-4 sm:px-5 whitespace-nowrap">{t('register')}<ArrowRight className={`w-3.5 h-3.5 ${isRTL ? 'rotate-180' : ''}`} /></button>
 </Link>
 </>
 )}

 <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 text-ink-800 hover:text-flame-600" data-testid="mobile-menu-btn" aria-label="Menu">
 <Menu className="w-5 h-5" />
 </button>
 </div>
 </div>
 </div>
 </header>

          {/* MEGA MENU — Vantage-style: drops directly under hovered nav item */}
          <AnimatePresence>
            {openMenu && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0, transition: { duration: 0.22, ease: [0.32, 0.72, 0, 1] } }}
                exit={{ opacity: 0, y: -6, transition: { duration: 0.16 } }}
                className="fixed z-40 pointer-events-auto hidden lg:block"
                style={{
                  top: 'calc(var(--vt-header-top, 0px) + 64px + 6px)',
                  left: isRTL ? undefined : `${menuLeft}px`,
                  right: isRTL ? `${window.innerWidth - menuLeft}px` : undefined,
                }}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
                data-testid="mega-menu-overlay"
              >
                {(() => {
                  const item = items.find((i) => i.id === openMenu);
                  if (!item) return null;
                  return (
                    <FlyoutPanel
                      rootItems={item.items}
                      rootKey={item.id}
                      isRTL={isRTL}
                      onSelect={() => setOpenMenu(null)}
                    />
                  );
                })()}
              </motion.div>
            )}
          </AnimatePresence>

          {/* MOBILE MENU — Vantage-style stack navigation with slide-down + slide-in */}
          <MobileMenu open={mobileOpen} onClose={() => setMobileOpen(false)} items={items} />

          {/* Global command-palette search */}
          <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
 </>
 );
};

export default Header;
